import { SubjectAttributes } from 'experimentation-client-js';
import { useCallback } from 'react';

import { useExperimentation } from './useExperimentation';

/**
 * Options for a single assignment evaluation.
 */
interface GetAssignmentOptions<T> {
    flagKey: string;
    subjectAttributes: Partial<SubjectAttributes>;
    defaultValue: T;
    type: 'boolean' | 'string' | 'number' | 'integer' | 'json';
}

interface UseGetFeatureFlagResult {
    /**
     * Evaluates a feature flag on demand. Waits for the client to be ready,
     * so it is safe to call from event handlers before initialization completes.
     */
    getAssignment: <T extends boolean | string | number | object = boolean>(
        options: GetAssignmentOptions<T>
    ) => Promise<T>;
}

export function useGetFeatureFlag(): UseGetFeatureFlagResult {
    const { syncClient, asyncClient, subjectAttributes: contextSubjectAttributes } = useExperimentation();

    const getAssignment = useCallback(
        async <T extends boolean | string | number | object = boolean>(
            options: GetAssignmentOptions<T>
        ): Promise<T> => {
            const { flagKey, subjectAttributes: subjectAttributesOverride, defaultValue, type } = options;

            const subjectAttributes = subjectAttributesOverride
                ? ({
                      ...contextSubjectAttributes,
                      ...subjectAttributesOverride,
                      extras: {
                          ...contextSubjectAttributes.extras,
                          ...subjectAttributesOverride.extras,
                      },
                  } as SubjectAttributes)
                : contextSubjectAttributes;

            try {
                const client = syncClient ?? (await asyncClient);

                if (!client) return defaultValue;

                switch (type) {
                    case 'boolean':
                        return client.getBooleanAssignment(flagKey, subjectAttributes, defaultValue as boolean) as T;
                    case 'string':
                        return client.getStringAssignment(flagKey, subjectAttributes, defaultValue as string) as T;
                    case 'number':
                        return client.getNumericAssignment(flagKey, subjectAttributes, defaultValue as number) as T;
                    case 'integer':
                        return client.getIntegerAssignment(flagKey, subjectAttributes, defaultValue as number) as T;
                    case 'json':
                        return client.getJSONAssignment(flagKey, subjectAttributes, defaultValue as object) as T;
                    default:
                        return defaultValue;
                }
            } catch {
                return defaultValue;
            }
        },
        [syncClient, asyncClient, contextSubjectAttributes]
    );

    return { getAssignment };
}
